import type { Action, ResourceOption } from '../types/roles';
import type { ResourceItem, ResourceType } from '../types/common';

export type ResourceLoader = () => Promise<ResourceItem[]>

export interface Resource {
    type: string;
    labelKey: string,
    actions: Action[],
    icon?: string,
    loader: ResourceLoader,
    permissionTypes: ResourceType[]
}

export const useResourcesStore = defineStore('resourcesStore', () => {
    const registeredResources = ref<Resource[]>([]);

    const getResource = (type: string): Resource | undefined => {
        return registeredResources.value.find(it => it.type === type);
    };

    const getPermissionResource = (type: ResourceType): Resource | undefined => {
        return registeredResources.value.find(it => it.permissionTypes.includes(type));
    };

    /**
     * Registers resource that permissions should be configurable for
     * @param type resource type identifier
     * @param resourceLabelKey localization label for resource name
     * @param availableActions actions that can be defined for each item
     * @param loader loader function that loads list of items to set permissions on
     * @param icon resource icon
     */
    function registerResource (type: string, resourceLabelKey: string, availableActions: Action[], loader: ResourceLoader,
        icon: string | undefined = undefined, permissionTypes: ResourceType[] = []
    ): Resource {
        const item: Resource = { type, labelKey: resourceLabelKey, actions: availableActions, loader, icon, permissionTypes };
        registeredResources.value.push(item);
        return item;
    }

    async function loadPermissionOptions (type: ResourceType): Promise<ResourceOption[]> {
        const resource = getPermissionResource(type);
        if (!resource) {
            return [];
        }

        return (await resource.loader()).map(item => ({
            value: item.permissionValue ?? item.key,
            text: item.permissionLabel ?? item.label
        }));
    }

    async function resolveResourceItems (items: {id: string, resourceType: string}[]): Promise<ResourceItem[]> {
        const requiredTypes = Array.from(new Set(items.map(it => it.resourceType)));

        const resources = Object.fromEntries(
            (await Promise.all(requiredTypes.map(it => getResource(it)!.loader())))
                .map((it, index) => ([requiredTypes[index], it] as [string, ResourceItem[]]))
        );

        return items.map(it => resources[it.resourceType]!.find(i => i.id === it.id)!);
    }

    return {
        resolveResourceItems,
        registerResource,
        registeredResources,
        getResource,
        loadPermissionOptions
    };
});
